import React, { Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/common/Errorboundry";
import PrivateRoute from "./components/common/PrivateRoute";
import Home from "./pages/public/Home";
import RootLayout from "./pages/public/RootLayout";
import SignUp from "./pages/public/SignUp";
import Login from "./pages/public/Login";
import { ForgotPassword } from "./pages/public/ForgotPassword";
import Dashboard from "./pages/admin/Dashboard/Dashboard";
import Profile from "./pages/admin/Dashboard/Profile";
import Search from "./pages/public/Search";
import CreateCompany from "./pages/public/CreateCompany";
import RootLayoutAdmin from "./pages/admin/RootLayoutAdmin";
import { CompanyProfile } from "./pages/public/Company/[id]";
import MyCompany from "./pages/admin/Dashboard/MyCompanies";
import Following from "./pages/admin/Dashboard/Following";
import Purchased from "./pages/admin/Dashboard/Purchased";
import { ClaimCompany } from "./pages/public/Company/[claimCompany]";
import Request from "./pages/admin/Dashboard/Request";
import CompanyEditProfile from "./pages/admin/CompanyEditProfile";
import { Help } from "./pages/admin/Dashboard/Help";
import { Payment } from "./components/public/Payment/Payment";
import { PaymentDetails } from "./components/public/Payment/Plans/PaymentDetails";
import { SuccessfulPayment } from "./components/public/Payment/Plans/SuccessfulPayment";
import Billing from "./pages/admin/Dashboard/Billing";
import { TrevexPrivacyPolicy } from "./pages/public/TrevexPrivacyPolicy";
import { TermsAndCondition } from "./pages/public/TermsAndcondition";
import Verification from "./components/common/Verification";
import Enterprise from "./pages/admin/Dashboard/Enterprise";
import EnterpriseActivityLogTable from "./components/admin/Dashboard/dashboard/EnterpriseActivityLogTable";
import { EnterpriseTrackedCompanies } from "./components/admin/Dashboard/mycompanies/EnterpriseTrackedCompanies";
import GptPrompts from "./pages/public/GptPrompts";
// import GPT_prompt from "./pages/public/GPT_prompt";
import { GPT_prompt } from "./components/admin/Dashboard/GPT_prompt";
import ThankYou from "./pages/public/ThankYou";
import TradeLicence from "./pages/public/Upload/TradeLicence";
import PaymentRefHub from "./pages/admin/Dashboard/PaymentRefHub";
import PaymentRefQnA from "./pages/admin/Dashboard/PaymentRefQnA";
import SDKIntegration from "./pages/public/SDKIntegration";
import PaymentScreen from "./components/public/VerifyAccount/PaymentScreen";
import Membership from "./pages/public/Membership"; // TIP-488 — reusable "Become a Member" page
import MembershipPlans from "./pages/public/MembershipPlans"; // TIP-647 — public, company-less membership page
import DashboardMembership from "./pages/admin/Dashboard/Membership"; // TIP-494 — dashboard membership page
import CounterpartyBriefs from "./pages/admin/Dashboard/CounterpartyBriefs"; // TIP-609 — counterparty briefs page
import TradeInformation from "./pages/admin/Dashboard/TradeInformation";
import TradeFinance from "./pages/admin/Dashboard/TradeFinance";
import TradeRisk from "./pages/admin/Dashboard/TradeRisk";

const AgentIntake = React.lazy(() => import("./components/public/AgentIntake"));
const RegisterCompany = React.lazy(() => import("./components/public/CompanyRegister"));
const VerifyAccount = React.lazy(() => import("./components/public/VerifyAccount"));
const CompanyRegistration = React.lazy(() => import("./components/public/CompanyRegistration"));

export const router = createBrowserRouter([
  {
    path: "/",
    children: [{ path: "/", element: <ErrorBoundary><PrivateRoute token={false}><Home /></PrivateRoute></ErrorBoundary> }],
  },
  {
    path: "gpt-prompts/access",
    element: <ErrorBoundary><PrivateRoute token={false}><GPT_prompt to="gpt"/></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/",
    element: <ErrorBoundary><PrivateRoute token={false}><RootLayout liftHeader={true} /></PrivateRoute></ErrorBoundary>,
    children: [
      { path: "/gpt-prompts", element: <ErrorBoundary><PrivateRoute token={false}><GPT_prompt to="gpt" /></PrivateRoute></ErrorBoundary> },
    ],
  },
  {
    path: "/",
    element: <ErrorBoundary><PrivateRoute token={false}><RootLayout /></PrivateRoute></ErrorBoundary>,
    children: [
      { path: "/", element: <ErrorBoundary><PrivateRoute token={false}><Home /></PrivateRoute></ErrorBoundary> },
      { path: "/search", element: <ErrorBoundary><PrivateRoute token={false}><Search /></PrivateRoute></ErrorBoundary> },
      { path: "/gpt-prompts/search", element: <ErrorBoundary><PrivateRoute token={false}><GPT_prompt /></PrivateRoute></ErrorBoundary> },
      { path: "/gpt-prompts/search/:id", element: <ErrorBoundary><PrivateRoute token={false}><GPT_prompt /></PrivateRoute></ErrorBoundary> },
      { path: "/search/:id", element: <ErrorBoundary><PrivateRoute ><CompanyProfile /></PrivateRoute></ErrorBoundary> },
      // TIP-556 — the Request Company form is sold as a web-profile/report
      // order, so it lives at /order-web-profile; the old URL redirects.
      { path: "/order-web-profile", element: <ErrorBoundary><PrivateRoute><CreateCompany /></PrivateRoute></ErrorBoundary> },
      { path: "/create-company", element: <Navigate to="/order-web-profile" replace /> },
      // TIP-647 — PUBLIC membership page. No company, no login required: a
      // visitor reads the perks and pricing, and the gate on Proceed handles
      // auth + claim. Inside RootLayout so a page that leads to payment carries
      // the site nav and the footer's Privacy Policy / Terms links.
      { path: "/membership", element: <ErrorBoundary><PrivateRoute token={false}><MembershipPlans /></PrivateRoute></ErrorBoundary> },
    ],
  },
  {
    path: "/update/trade-license/:token",
    element: <ErrorBoundary><PrivateRoute token={false}><TradeLicence /></PrivateRoute></ErrorBoundary>,
  },
  {
    // Public route for external agents — authenticated by the 6-char access
    // code inside AgentIntakePage, NOT by the site-wide verification gate
    // (which is consumer-flow only). PrivateRoute deliberately omitted.
    // Lazy-loaded so AgentIntake's CSS/components don't ship in the main bundle.
    path: "/agent-intake/:token",
    element: <ErrorBoundary><Suspense fallback={null}><AgentIntake /></Suspense></ErrorBoundary>,
  },
  {
    // TIP-516 — direct company registration. Same agent-code gate as
    // /agent-intake, but started BLANK (no token in the URL): the page mints a
    // fresh draft Lead on entry and reuses the intake form. PrivateRoute /
    // verification gate deliberately omitted (agent-authenticated, like above).
    path: "/register-company",
    element: <ErrorBoundary><Suspense fallback={null}><RegisterCompany /></Suspense></ErrorBoundary>,
  },
  {
    // TIP-453 — nominee-facing Verify Account page. Token in the URL is the
    // auth credential; PrivateRoute / verification gate skipped for the same
    // reason as the agent intake route above.
    path: "/verify-account/:token",
    element: <ErrorBoundary><Suspense fallback={null}><VerifyAccount /></Suspense></ErrorBoundary>,
  },
  {
    // TIP-581 — agent-shared prefilled self-registration. The prospect opens
    // this link (shared live on the call), edits the pre-filled intake form,
    // sets a password, and submits. Token in the URL is the credential; no
    // agent gate / verification gate (TIP-579 backend, product-approved).
    path: "/company-registration/:token",
    element: <ErrorBoundary><Suspense fallback={null}><CompanyRegistration /></Suspense></ErrorBoundary>,
  },
  {
    path: "/payments",
    element: <ErrorBoundary><Suspense fallback={null}><PaymentScreen /></Suspense></ErrorBoundary>,
  },
  {
    // TIP-488 — reusable "Become a Member" page. Auth-gated (membership purchase
    // requires a signed-in user). companyId in the URL is the company to enroll.
    path: "/membership/:companyId",
    element: <ErrorBoundary><PrivateRoute><Membership /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/claim/:id",
    element: <ErrorBoundary><PrivateRoute><ClaimCompany /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/sign-up",
    element: <ErrorBoundary><PrivateRoute token={false}><SignUp /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/thank-you",
    children: [{ path: "/thank-you", element: <ErrorBoundary><PrivateRoute token={false}><ThankYou /></PrivateRoute></ErrorBoundary> }],
  },
  {
    path: "/sdk",
    element: <ErrorBoundary><PrivateRoute token={false}><SDKIntegration /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/login",
    element: <ErrorBoundary><PrivateRoute token={false} isLogin><Login /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/reset-password",
    element: <ErrorBoundary><PrivateRoute token={false}><ForgotPassword route={"reset"} /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/set-password",
    element: <ErrorBoundary><PrivateRoute token={false}><ForgotPassword route={"set"} /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/plans",
    element: <ErrorBoundary><PrivateRoute token={false}><Payment /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/plans/paymentdetails",
    element: <ErrorBoundary><PrivateRoute><PaymentDetails /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/plans/paymentsuccessful",
    element: <ErrorBoundary><PrivateRoute><SuccessfulPayment /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/dashboard",
    element: <ErrorBoundary><PrivateRoute><RootLayoutAdmin /></PrivateRoute></ErrorBoundary>,
    children: [
      { path: "/dashboard", element: <ErrorBoundary><PrivateRoute><Dashboard /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/profile", element: <ErrorBoundary><PrivateRoute><Profile /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/my-companies", element: <ErrorBoundary><PrivateRoute><MyCompany /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/membership", element: <ErrorBoundary><PrivateRoute><DashboardMembership /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/counterparty-briefs", element: <ErrorBoundary><PrivateRoute><CounterpartyBriefs /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/trade-information", element: <ErrorBoundary><PrivateRoute><TradeInformation /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/trade-finance", element: <ErrorBoundary><PrivateRoute><TradeFinance /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/trade-risk", element: <ErrorBoundary><PrivateRoute><TradeRisk /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/enterprise", element: <ErrorBoundary><PrivateRoute><Enterprise /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/enterprise/:id", element: <ErrorBoundary><PrivateRoute><EnterpriseTrackedCompanies /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/enterprise/act-log", element: <ErrorBoundary><PrivateRoute><EnterpriseActivityLogTable /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/following", element: <ErrorBoundary><PrivateRoute><Following /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/purchased", element: <ErrorBoundary><PrivateRoute><Purchased /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/payment-ref", element: <ErrorBoundary><PrivateRoute><PaymentRefHub /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/payment-ref/form", element: <ErrorBoundary><PrivateRoute><PaymentRefQnA /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/request", element: <ErrorBoundary><PrivateRoute><Request /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/billing", element: <ErrorBoundary><PrivateRoute><Billing /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/help", element: <ErrorBoundary><PrivateRoute><Help /></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/gpt", element: <ErrorBoundary><PrivateRoute><GPT_prompt to={"gpt"}/></PrivateRoute></ErrorBoundary> },
      { path: "/dashboard/article", element: <ErrorBoundary><PrivateRoute><GPT_prompt to={"article"} /></PrivateRoute></ErrorBoundary> },

    ],
  },
  {
    path: "/edit/:id",
    element: <ErrorBoundary><PrivateRoute><CompanyEditProfile /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/privacy-policy",
    element: <ErrorBoundary><PrivateRoute token={false}><TrevexPrivacyPolicy /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/terms-and-condition",
    element: <ErrorBoundary><PrivateRoute token={false}><TermsAndCondition /></PrivateRoute></ErrorBoundary>,
  },
  {
    path: "/verification",
    element: (
      <ErrorBoundary><Verification /></ErrorBoundary>
    ),
  },
  {
    path: "*",
    element: (
      <ErrorBoundary><PrivateRoute token={false}>
        <Login />
      </PrivateRoute></ErrorBoundary>
    ),
  },
]);
